import { useId, useState } from 'react';
import type * as React from 'react';
import { useCreate, useUpdate } from '@refinedev/core';
import { Plus } from 'lucide-react';
import { Page, Notice } from '@/components/page';
import { DataTable, type Column } from '@/components/data-table';
import { EmptyState } from '@/components/states';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogForm,
  DialogBody,
  DialogContent,
  DialogFooter,
} from '@/components/ui/dialog';
import { FormField, Input } from '@/components/ui/field';
import { Badge, Mono } from '@/components/ui/primitives';
import { RESOURCES, type AdminMarketplace } from '@/api/resources';
import { useAdminList } from '@/hooks/use-admin-resource';
import { ApiError } from '@/api/errors';
import { notify } from '@/lib/notify';
import { formatNumber } from '@/lib/format';

/**
 * Marketplaces (06 §2).
 *
 * ⚠️ **Changing a partner tag does not rewrite existing `products.affiliate_url`.**
 * Every link already stored keeps the old tag until it is re-derived from
 * `raw_url`, which is why `rawUrl` is stored alongside `affiliateUrl` at all
 * (CLAUDE.md §4.4). The change is therefore a typed confirmation, and the
 * dialog says plainly what it does and does not do — an admin who assumes it is
 * retroactive will quietly lose commission on every existing link.
 */
export function MarketplacesPage(): React.JSX.Element {
  const { rows, isLoading, error, refetch, invalidate } = useAdminList<AdminMarketplace>(
    RESOURCES.marketplaces,
  );
  const [editing, setEditing] = useState<AdminMarketplace | 'new' | null>(null);

  const columns: Array<Column<AdminMarketplace>> = [
    {
      key: 'code',
      header: 'Code',
      width: '5.5rem',
      sortValue: (row) => row.code,
      render: (row) => <Mono>{row.code}</Mono>,
    },
    {
      key: 'domain',
      header: 'Domain',
      sortValue: (row) => row.domain,
      render: (row) => (
        <button
          type="button"
          className="rounded text-left text-sm font-medium text-ink hover:underline"
          onClick={() => setEditing(row)}
        >
          {row.domain}
        </button>
      ),
    },
    {
      key: 'hostnames',
      header: 'Allowlist',
      hideBelow: 'lg',
      render: (row) =>
        row.hostnames.length === 0 ? (
          <span className="text-xs text-ink-faint">None</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {row.hostnames.map((hostname) => (
              <Mono key={hostname}>{hostname}</Mono>
            ))}
          </span>
        ),
    },
    {
      key: 'partnerTag',
      header: 'Partner tag',
      render: (row) =>
        row.partnerTag ? (
          <Mono>{row.partnerTag}</Mono>
        ) : (
          <span className="text-xs text-ink-faint">Not an affiliate</span>
        ),
    },
    {
      key: 'currency',
      header: 'Currency',
      hideBelow: 'md',
      render: (row) => row.currency ?? <span className="text-xs text-ink-faint">—</span>,
    },
    {
      key: 'usageCount',
      header: 'Products',
      align: 'right',
      sortValue: (row) => row.usageCount,
      render: (row) => formatNumber(row.usageCount),
    },
    {
      key: 'status',
      header: 'Status',
      align: 'right',
      render: (row) =>
        row.isActive ? (
          <Badge tone="success">Active</Badge>
        ) : (
          <Badge tone="neutral">Inactive</Badge>
        ),
    },
  ];

  return (
    <Page
      title="Marketplaces"
      description="The domain allowlist a pasted product URL is matched against, and the partner tag each one is built with."
      breadcrumbs={[{ label: 'Master data' }, { label: 'Marketplaces' }]}
      width="wide"
      action={
        <Button variant="primary" onClick={() => setEditing('new')}>
          <Plus aria-hidden="true" />
          Add marketplace
        </Button>
      }
    >
      <div className="space-y-5">
        <Notice title="A new partner tag applies to new links only">
          Existing <Mono>products.affiliate_url</Mono> rows keep the tag they were built with. Links
          are re-derived from <Mono>raw_url</Mono> by a separate job, not by saving this form.
        </Notice>

        <DataTable
          label="Marketplaces"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          isLoading={isLoading}
          error={error}
          onRetry={refetch}
          endpoint="GET /admin/marketplaces"
          empty={
            <EmptyState
              title="No marketplaces configured"
              description="Without a marketplace no pasted URL can be validated, so wishlist items cannot be added."
              action={
                <Button variant="primary" onClick={() => setEditing('new')}>
                  <Plus aria-hidden="true" />
                  Add marketplace
                </Button>
              }
            />
          }
        />
      </div>

      {editing ? (
        <MarketplaceDialog
          marketplace={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate();
          }}
        />
      ) : null}
    </Page>
  );
}

// ---------------------------------------------------------------------------

function MarketplaceDialog({
  marketplace,
  onClose,
  onSaved,
}: {
  marketplace: AdminMarketplace | null;
  onClose: () => void;
  onSaved: () => void;
}): React.JSX.Element {
  const [code, setCode] = useState(marketplace?.code ?? '');
  const [domain, setDomain] = useState(marketplace?.domain ?? '');
  const [hostnames, setHostnames] = useState((marketplace?.hostnames ?? []).join('\n'));
  const [partnerTag, setPartnerTag] = useState(marketplace?.partnerTag ?? '');
  const [currency, setCurrency] = useState(marketplace?.currency ?? '');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const [confirmingTag, setConfirmingTag] = useState(false);

  const codeId = useId();
  const domainId = useId();
  const hostnamesId = useId();
  const tagId = useId();
  const currencyId = useId();

  const { mutateAsync: create } = useCreate();
  const { mutateAsync: update } = useUpdate();

  const tagChanged = Boolean(marketplace) && partnerTag.trim() !== (marketplace?.partnerTag ?? '');

  const save = async (): Promise<void> => {
    setPending(true);
    setFieldErrors({});

    const parsedHostnames = hostnames
      .split(/[\n,]/)
      .map((host) => host.trim())
      .filter(Boolean);

    try {
      if (marketplace) {
        await update({
          resource: RESOURCES.marketplaces,
          id: marketplace.id,
          values: {
            hostnames: parsedHostnames,
            partnerTag: partnerTag.trim() || undefined,
            currency: currency.trim() || undefined,
          },
          successNotification: false,
          errorNotification: false,
        });
        notify.success('Saved', marketplace.domain);
      } else {
        await create({
          resource: RESOURCES.marketplaces,
          values: {
            code: code.trim().toUpperCase(),
            domain: domain.trim(),
            hostnames: parsedHostnames,
            partnerTag: partnerTag.trim() || undefined,
            currency: currency.trim() || undefined,
          },
          successNotification: false,
          errorNotification: false,
        });
        notify.success('Added', domain.trim());
      }
      setConfirmingTag(false);
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) setFieldErrors(err.fieldErrors);
      notify.error(err, 'The marketplace was not saved');
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <DialogContent
          title={marketplace ? `Edit ${marketplace.domain}` : 'Add marketplace'}
          description={
            marketplace
              ? 'The code and domain are fixed once created — products reference them.'
              : 'The code is immutable once created.'
          }
        >
          <DialogForm
            onSubmit={(event) => {
              event.preventDefault();
              if (tagChanged) setConfirmingTag(true);
              else void save();
            }}
            noValidate
          >
            <DialogBody>
              {marketplace ? null : (
                <>
                  <FormField
                    label="Code"
                    htmlFor={codeId}
                    hint="Uppercase letters, e.g. US or UK. Immutable."
                    error={fieldErrors['code']}
                    required
                  >
                    <Input
                      value={code}
                      onChange={(event) => setCode(event.target.value.toUpperCase())}
                      className="font-mono"
                      autoFocus
                      required
                    />
                  </FormField>

                  <FormField
                    label="Domain"
                    htmlFor={domainId}
                    error={fieldErrors['domain']}
                    required
                  >
                    <Input
                      value={domain}
                      onChange={(event) => setDomain(event.target.value)}
                      placeholder="amazon.co.uk"
                      required
                    />
                  </FormField>
                </>
              )}

              <FormField
                label="Hostname allowlist"
                htmlFor={hostnamesId}
                hint="One per line. A pasted URL is only accepted if its host is on this list."
                error={fieldErrors['hostnames']}
              >
                <textarea
                  value={hostnames}
                  onChange={(event) => setHostnames(event.target.value)}
                  rows={3}
                  spellCheck={false}
                  className="w-full rounded-md border border-line-strong bg-surface px-3 py-2 font-mono text-xs text-ink"
                />
              </FormField>

              <FormField
                label="Partner tag"
                htmlFor={tagId}
                hint="Leave empty for a marketplace with no affiliate programme."
                error={fieldErrors['partnerTag']}
              >
                <Input
                  value={partnerTag}
                  onChange={(event) => setPartnerTag(event.target.value)}
                  className="font-mono"
                  placeholder="babysesame-20"
                />
              </FormField>

              <FormField
                label="Currency"
                htmlFor={currencyId}
                hint="Three-letter ISO 4217 code."
                error={fieldErrors['currency']}
              >
                <Input
                  value={currency}
                  onChange={(event) => setCurrency(event.target.value.toUpperCase().slice(0, 3))}
                  className="font-mono"
                  placeholder="GBP"
                />
              </FormField>
            </DialogBody>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={pending}>
                {marketplace ? 'Save changes' : 'Add marketplace'}
              </Button>
            </DialogFooter>
          </DialogForm>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmingTag}
        onOpenChange={setConfirmingTag}
        title="Change the partner tag?"
        description={
          <>
            New links will be built with <Mono>{partnerTag.trim() || 'no tag'}</Mono>.{' '}
            <strong className="text-ink">
              Links already stored keep the old tag until they are re-derived from raw_url.
            </strong>{' '}
            Commission on existing links continues to go to{' '}
            <Mono>{marketplace?.partnerTag ?? 'no tag'}</Mono> until that runs.
          </>
        }
        confirmLabel="Change partner tag"
        typedConfirmation={marketplace?.code}
        loading={pending}
        onConfirm={() => void save()}
      />
    </>
  );
}
